import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Calendar } from '@/components/ui/shadcn/calendar';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { MultiSelect } from '@/components/ui/shadcn/multi-select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { Switch } from '@/components/ui/shadcn/switch';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { businessNow, businessTimeNow, businessToday, isAfterBusinessToday } from '@/lib/business-time';
import { cn } from '@/lib/utils';
import type { AddPhoneCallModalProps, PhoneCallFormData } from '@/types/forms/companies';
import { useForm } from '@inertiajs/react';
import { format } from 'date-fns';
import { Building2, Calendar as CalendarIcon, Clock, Phone } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

export default function AddPhoneCallModal({
    company,
    users = [],
    trigger,
    phoneCall,
    defaultParticipantIds,
    open: controlledOpen,
    onOpenChange,
}: AddPhoneCallModalProps) {
    const { t } = useTranslations();
    const isEdit = Boolean(phoneCall);
    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = (value: boolean) => {
        if (isControlled) {
            onOpenChange?.(value);
        } else {
            setInternalOpen(value);
        }
    };
    const [date, setDate] = useState<Date>(businessNow());

    const { data, setData, post, put, processing, errors, reset } = useForm<PhoneCallFormData>({
        description: '',
        call_date: businessToday(),
        call_time: businessTimeNow(),
        call_direction: 'outbound',
        call_result: '',
        duration_minutes: '',
        participants: [],
        create_follow_up: false,
        follow_up_date: '',
        follow_up_note: '',
    });

    const userOptions = useMemo(
        () =>
            users.map((user) => ({
                value: user.id.toString(),
                label: `${user.first_name} ${user.last_name}`,
            })),
        [users],
    );

    const descriptionLength = data.description?.length ?? 0;

    // In edit mode, populate the form from the phone call whenever the dialog opens.
    useEffect(() => {
        if (!open || !phoneCall) {
            return;
        }

        const parsedDate = phoneCall.call_date ? new Date(phoneCall.call_date) : businessNow();

        setData({
            description: phoneCall.description ?? '',
            call_date: phoneCall.call_date,
            call_time: (phoneCall.call_time ?? '').slice(0, 5),
            call_direction: phoneCall.call_direction ?? 'outbound',
            call_result: phoneCall.call_result ?? '',
            duration_minutes: phoneCall.duration_minutes != null ? String(phoneCall.duration_minutes) : '',
            participants: phoneCall.participantIds,
            create_follow_up: Boolean(phoneCall.follow_up_date || phoneCall.follow_up_note),
            follow_up_date: phoneCall.follow_up_date ?? '',
            follow_up_note: phoneCall.follow_up_note ?? '',
        });
        if (!Number.isNaN(parsedDate.getTime())) {
            setDate(parsedDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, phoneCall]);

    // In create mode, pre-select the contact(s) whose page opened the modal.
    useEffect(() => {
        if (!open || phoneCall || !defaultParticipantIds?.length) {
            return;
        }
        setData('participants', defaultParticipantIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, phoneCall]);

    const handleDateSelect = (selectedDate: Date | undefined) => {
        if (!selectedDate) {
            return;
        }

        setDate(selectedDate);
        setData('call_date', format(selectedDate, 'yyyy-MM-dd'));
    };

    const submit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!company) return;

        if (isEdit && phoneCall) {
            put(route('company.phone-call.update', [company.id, phoneCall.id]), {
                preserveScroll: true,
                onSuccess: () => setOpen(false),
            });

            return;
        }

        post(route('company.phone-call.store', company.id), {
            onSuccess: () => {
                setOpen(false);
                reset();
                setDate(businessNow());
            },
        });
    };

    const handleOpenChange = (isOpen: boolean) => {
        setOpen(isOpen);
        if (!isOpen) {
            reset();
            setDate(businessNow());
            setData('call_date', businessToday());
            setData('call_time', businessTimeNow());
            setData('call_direction', 'outbound');
            setData('create_follow_up', false);
            setData('follow_up_date', '');
            setData('follow_up_note', '');
        }
    };

    return (
        <ResponsiveModal open={open} onOpenChange={handleOpenChange} variant="sheet">
            {trigger ? (
                <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger>
            ) : !isControlled ? (
                <ResponsiveModalTrigger asChild>
                    <Button variant="outline" size="sm">
                        <Phone className="mr-2 h-4 w-4" />
                        {t('companies.forms.add_phone_call.buttons.add_phone_call', 'Pridėti skambutį')}
                    </Button>
                </ResponsiveModalTrigger>
            ) : null}
            <ResponsiveModalContent size="xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>
                        {isEdit
                            ? t('companies.forms.add_phone_call.edit_title', 'Redaguoti skambutį')
                            : t('companies.forms.add_phone_call.title', 'Pridėti skambutį')}
                    </ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('companies.forms.add_phone_call.description', 'Užregistruokite įvykusį telefono skambutį su kliento kontaktu.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-4">
                        <div className="bg-muted/30 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                            <Building2 className="h-4 w-4" />
                            <span>
                                {t('companies.forms.add_phone_call.labels.company_context', 'Įmonė: :name').replace(':name', company?.name || '—')}
                            </span>
                        </div>

                        <div className="space-y-2">
                            <Label>
                                {t('companies.forms.add_phone_call.labels.participants', 'Kontaktinis asmuo')}{' '}
                                <span className="text-destructive">*</span>
                            </Label>
                            <MultiSelect
                                options={userOptions}
                                selected={data.participants}
                                onChange={(selected) => setData('participants', selected)}
                                placeholder={t('companies.forms.add_phone_call.placeholders.select_participants', 'Pasirinkite kontaktą...')}
                                searchPlaceholder={t('companies.forms.add_phone_call.placeholders.search_users', 'Ieškoti kontaktų...')}
                                emptyText={t('companies.forms.add_phone_call.placeholders.no_users', 'Kontaktų nerasta.')}
                                className={errors.participants ? 'border-destructive' : ''}
                            />
                            {errors.participants && <p className="text-destructive text-sm">{errors.participants}</p>}
                        </div>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label>
                                    {t('companies.forms.add_phone_call.labels.call_date', 'Skambučio data')}{' '}
                                    <span className="text-destructive">*</span>
                                </Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className={cn(
                                                'w-full justify-start text-left font-normal',
                                                !date && 'text-muted-foreground',
                                                errors.call_date && 'border-destructive',
                                            )}
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {date
                                                ? format(date, 'yyyy-MM-dd')
                                                : t('companies.forms.add_phone_call.placeholders.pick_date', 'Pasirinkite datą')}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0">
                                        <Calendar
                                            mode="single"
                                            selected={date}
                                            onSelect={handleDateSelect}
                                            disabled={isAfterBusinessToday}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                                {errors.call_date && <p className="text-destructive text-sm">{errors.call_date}</p>}
                            </div>

                            <div className="min-w-0 space-y-2">
                                <Label htmlFor="call_time">{t('companies.forms.add_phone_call.labels.call_time', 'Skambučio laikas')}</Label>
                                <div className="relative">
                                    <Clock className="text-muted-foreground pointer-events-none absolute top-2.5 left-3 h-4 w-4" />
                                    <Input
                                        id="call_time"
                                        type="time"
                                        value={data.call_time || ''}
                                        onChange={(e) => setData('call_time', e.target.value)}
                                        className={cn('pl-9', errors.call_time ? 'border-destructive' : '')}
                                    />
                                </div>
                                {errors.call_time && <p className="text-destructive text-sm">{errors.call_time}</p>}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>{t('companies.forms.add_phone_call.labels.call_direction', 'Skambučio kryptis')}</Label>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { value: 'outbound', label: t('companies.forms.add_phone_call.options.outbound', 'Išeinantis') },
                                    { value: 'inbound', label: t('companies.forms.add_phone_call.options.inbound', 'Įeinantis') },
                                ].map((option) => (
                                    <Button
                                        key={option.value}
                                        type="button"
                                        variant={data.call_direction === option.value ? 'default' : 'outline'}
                                        onClick={() => setData('call_direction', option.value)}
                                    >
                                        {option.label}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="call_result">{t('companies.forms.add_phone_call.labels.call_result', 'Skambučio rezultatas')}</Label>
                            <select
                                id="call_result"
                                value={data.call_result || ''}
                                onChange={(e) => setData('call_result', e.target.value)}
                                className={cn(
                                    'border-input bg-background flex h-9 w-full rounded-md border px-3 py-1 text-sm',
                                    errors.call_result ? 'border-destructive' : '',
                                )}
                            >
                                <option value="">
                                    {t('companies.forms.add_phone_call.placeholders.select_call_result', 'Pasirinkite rezultatą...')}
                                </option>
                                <option value="connected">{t('companies.forms.add_phone_call.options.connected', 'Pavyko susisiekti')}</option>
                                <option value="no_answer">{t('companies.forms.add_phone_call.options.no_answer', 'Neatsiliepė')}</option>
                                <option value="callback_requested">
                                    {t('companies.forms.add_phone_call.options.callback_requested', 'Paprašė perskambinti')}
                                </option>
                                <option value="wrong_contact">
                                    {t('companies.forms.add_phone_call.options.wrong_contact', 'Netinkamas kontaktas')}
                                </option>
                            </select>
                            {errors.call_result && <p className="text-destructive text-sm">{errors.call_result}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="duration_minutes">{t('companies.forms.add_phone_call.labels.duration_minutes', 'Trukmė (min.)')}</Label>
                            <Input
                                id="duration_minutes"
                                type="number"
                                min={0}
                                max={600}
                                value={data.duration_minutes || ''}
                                onChange={(e) => setData('duration_minutes', e.target.value)}
                                className={errors.duration_minutes ? 'border-destructive' : ''}
                            />
                            {errors.duration_minutes && <p className="text-destructive text-sm">{errors.duration_minutes}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="call_description">
                                {t('companies.forms.add_phone_call.labels.call_notes', 'Skambučio užrašai')}{' '}
                                <span className="text-destructive">*</span>
                            </Label>
                            <Textarea
                                id="call_description"
                                value={data.description}
                                onChange={(e) => setData('description', e.target.value)}
                                placeholder={t(
                                    'companies.forms.add_phone_call.placeholders.phone_call_notes',
                                    'Trumpai aprašykite, kas buvo aptarta arba kodėl skambutis neįvyko...',
                                )}
                                rows={5}
                                className={errors.description ? 'border-destructive' : ''}
                            />
                            <div className="text-muted-foreground text-right text-xs">{descriptionLength} / 2000</div>
                            {errors.description && <p className="text-destructive text-sm">{errors.description}</p>}
                        </div>

                        <div className="space-y-3 border-t pt-4">
                            <div className="flex items-center gap-3">
                                <Switch
                                    id="call-create-follow-up"
                                    checked={data.create_follow_up}
                                    onCheckedChange={(checked) => setData('create_follow_up', checked)}
                                />
                                <Label htmlFor="call-create-follow-up" className="text-sm font-medium">
                                    {t('companies.forms.follow_up.labels.create', 'Sukurti tolimesnį veiksmą')}
                                </Label>
                            </div>

                            {data.create_follow_up ? (
                                <div className="bg-muted/20 grid grid-cols-1 gap-3 rounded-lg border p-3 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="call_follow_up_date">
                                            {t('companies.forms.follow_up.labels.date', 'Tolimesnio veiksmo data')}
                                        </Label>
                                        <DatePicker
                                            value={data.follow_up_date}
                                            onValueChange={(value) => setData('follow_up_date', value)}
                                            placeholder={t('companies.forms.follow_up.placeholders.date', 'Pasirinkite datą')}
                                            contentSide="top"
                                            className={errors.follow_up_date ? 'border-destructive' : ''}
                                        />
                                        {errors.follow_up_date && <p className="text-destructive text-sm">{errors.follow_up_date}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="call_follow_up_note">{t('companies.forms.follow_up.labels.note', 'Pastaba')}</Label>
                                        <Textarea
                                            id="call_follow_up_note"
                                            value={data.follow_up_note}
                                            onChange={(event) => setData('follow_up_note', event.target.value)}
                                            placeholder={t('companies.forms.follow_up.placeholders.note', 'Trumpa pastaba...')}
                                            rows={2}
                                            className={errors.follow_up_note ? 'border-destructive' : ''}
                                        />
                                        {errors.follow_up_note && <p className="text-destructive text-sm">{errors.follow_up_note}</p>}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                            {t('companies.forms.add_phone_call.buttons.cancel', 'Atšaukti')}
                        </Button>
                        <Button type="submit" disabled={processing}>
                            {processing
                                ? t('companies.forms.add_phone_call.buttons.saving', 'Išsaugoma...')
                                : isEdit
                                  ? t('companies.forms.add_phone_call.buttons.update_phone_call', 'Atnaujinti skambutį')
                                  : t('companies.forms.add_phone_call.buttons.save_phone_call', 'Išsaugoti skambutį')}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
